import {
    Accordion,
    AccordionSummary,
    ExpandMoreIcon,
    AccordionDetails,
    PlumeIcon,
    useTheme,
    Alert,
    Button,
    Box,
    Tab,
    TabPanel
} from '@methanesat/ui-components';
import { DataRowsModalBody } from '../../../EmissionsPageMap/LayerDataDrawer/Layout';
import { analytics, getColorRangeColors, ErrorBoundary, handleErrorBoundaryError } from '../../../../utils';
import { DistinctPointSourceAccordionProps } from '../../../../types';
import { useGetPointSourceAccordionProps } from './utils';
import { useTranslate } from '../../../../hooks';
import { OGITitle } from '../DataSpecific/OGINearEmissions/OGITitle';
import { useEffect, useState, SyntheticEvent } from 'react';
import { EmissionTitle } from './EmissionTitle';
import StyledTabs from '../../../StyledTabs';

export const DistinctPointSourceAccordion = ({
    handleChange,
    expanded,
    info,
    ogiNearbyEnabled
}: DistinctPointSourceAccordionProps) => {
    const [ogiError, setOgiError] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);

    const theme = useTheme();
    const t = useTranslate();
    const colorRangeIconColors = getColorRangeColors(theme, true);

    const { id, title, emissionsHeadline, dataRows, platform, ogiContent, detailsContent } =
        useGetPointSourceAccordionProps(info, ogiError);

    useEffect(() => {
        if (expanded) {
            analytics.expandPointSourceAccordion({
                id: info.object.id,
                emissionRate: info.object.properties.flux,
                latitude: info.object.geometry.coordinates[1],
                longitude: info.object.geometry.coordinates[0],
                platform
            });
        }
    }, [expanded, info, platform]);

    const [longitude, latitude] = info.object.geometry.coordinates;

    const handleTabChange = (event: SyntheticEvent, selectedTab: number) => {
        setCurrentTab(selectedTab);
    };

    const tabs = [t('emissionsMapPage.drawerWithCards.tabs.details'), t('emissionsMapPage.drawerWithCards.tabs.ogi')];

    return (
        <Accordion
            key={id}
            expanded={expanded}
            onChange={() => {
                handleChange(id, info);
            }}
            data-testid={`point-source-accordion-${id}`}
            elevation={0}
            disableGutters
            sx={theme.mixins.emissionAccordion}
            disabled={!expanded && ogiNearbyEnabled}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ padding: '0' }}>
                <EmissionTitle
                    Icon={
                        <PlumeIcon
                            colors={colorRangeIconColors}
                            sx={(theme) => ({
                                marginRight: theme.mixins.emissionsDrawerIcon.marginRight
                            })}
                            width={theme.mixins.emissionsDrawerIcon.width}
                            height={theme.mixins.emissionsDrawerIcon.height}
                        />
                    }
                    emissionTypeLabel={title}
                    emissionsHeadline={emissionsHeadline}
                    data-testid={`point-source-accordion-title-${id}`}
                />
            </AccordionSummary>

            {/* padding accounts for the icon width + marginRight */}
            <AccordionDetails
                sx={(theme) => ({
                    pl: `calc(${theme.mixins.emissionsDrawerIcon.width}px + ${theme.mixins.emissionsDrawerIcon.marginRight})`
                })}
            >
                <Box sx={{ width: '100%' }}>
                    <StyledTabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="tabs"
                        data-testid="dispersed-tabs"
                        sx={{
                            ...(currentTab === 0 && {
                                '& .MuiTabs-indicator': {
                                    backgroundPosition: 'bottom left'
                                }
                            })
                        }}
                    >
                        {tabs.map((tab, index) => {
                            return (
                                <Tab
                                    label={tab}
                                    key={index}
                                    disabled={
                                        tab !== t('emissionsMapPage.drawerWithCards.tabs.ogi') && ogiNearbyEnabled
                                    }
                                />
                            );
                        })}
                    </StyledTabs>
                    <TabPanel value={currentTab} index={0}>
                        <DataRowsModalBody dataRows={dataRows} />
                        {detailsContent}
                    </TabPanel>

                    <ErrorBoundary
                        key={info.object.id}
                        onError={() => {
                            setOgiError(true);
                            handleErrorBoundaryError(`PlumeDrawerOGITitle: ${info.object.id}`);
                        }}
                        fallbackRender={({ resetErrorBoundary }) => {
                            return (
                                <Alert
                                    action={
                                        <Button
                                            onClick={() => {
                                                resetErrorBoundary();
                                                setOgiError(false);
                                            }}
                                            data-testid="retry-ogc-fetch-button"
                                            size="small"
                                        >
                                            {t('emissionsMapPage.error.retry')}
                                        </Button>
                                    }
                                    severity="error"
                                >
                                    {t('emissionsMapPage.error.general')}
                                </Alert>
                            );
                        }}
                    >
                        <TabPanel value={currentTab} index={1}>
                            <OGITitle source="disperesed" longitude={longitude} latitude={latitude} />
                            {ogiContent}
                        </TabPanel>
                    </ErrorBoundary>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
